import React from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { RouterPaths } from '@/services/shared/ApiEndpoints';
import { MediaType } from '@/types/MediaType';
import { calculateMonthsBetween } from '@/utils/calculateMonthsBetween';
import CircleProgress from './CircleProgress/CircleProgress';
import styles from './WalletCard.module.css';

type WalletCardType = {
  id: string;
  mediaType: string;
  year: string;
  description: string;
  supplierName: string;
  percentageRealizationProgress?: number;
  agreementStartDate?: string;
  agreementEndDate?: string;
};

const WalletCard = ({
  id,
  mediaType,
  year,
  description,
  supplierName,
  percentageRealizationProgress = 0,
  agreementStartDate,
  agreementEndDate,
}: WalletCardType) => {
  const { t } = useTranslation('wallet');
  const router = useRouter();

  const handleCardClick = () => {
    router.push(RouterPaths.WALLET_DETAILS(id));
  };

  const icon =
    mediaType === MediaType.ENERGY
      ? '/energy_icon.svg'
      : mediaType === MediaType.GAS
        ? '/gas_icon.svg'
        : '/default_icon.svg';

  const calculateActualPercentage = () => {
    if (!agreementStartDate || !agreementEndDate) {
      // If dates are not available, use the backend value as is (assumes 12 months)
      return (percentageRealizationProgress * 100).toFixed();
    }

    const totalMonths = calculateMonthsBetween(agreementStartDate, agreementEndDate);
    const adjustedPercentage = (percentageRealizationProgress * 12 / totalMonths) * 100;
    return adjustedPercentage.toFixed();
  };

  return (
    <div className={styles.container} onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div className={styles.header}>
        <div className={styles.iconContainer}>
          <img alt={`${mediaType} icon`} src={icon} />
        </div>
        <div>
          <strong>{t(mediaType)}</strong> {year} <br />
          <span>{description}</span>
        </div>
      </div>

      <div className={styles.cardContentBox}>
        <div className={styles.info}>
          <div>
            <p>{t('supplier')}</p>
            <p>{supplierName}</p>
          </div>
        </div>
        <div>
          <CircleProgress percentage={calculateActualPercentage()} size={100} />
        </div>
      </div>
    </div>
  );
};

export default WalletCard;